import { STORE_KEYS } from "./StoreKeys";
import TaskContextMenu from "@/react/components/entityViews/contextMenus/task/TaskContextMenu";
import ResourceContextMenu from "@/react/components/entityViews/contextMenus/resource/ResourceContextMenu";
import ClientContextMenu from "@/react/components/entityViews/contextMenus/client/ClientContextMenu";
import { EntityRecord } from "@/types/entity";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";

import { Building2, MapPin, Layers, Activity, Users, Milestone, GitBranch, Globe, ArrowRight } from "lucide-react";
import type { TileColor } from "@/react/components/entityViews/dashboardConfigurator/types";
import { EntityMediaView } from "@/react/components/entityViews/media/EntityMediaView";
import { EntityDocumentView } from "@/react/components/entityViews/documents/EntityDocumentView";
import { EntityNotesView } from "@/react/components/entityViews/notes/EntityNotesView";
import { EntityChecklistView } from "@/react/components/entityViews/checklist/EntityChecklistView";
import { EntityDescription } from "@/react/components/entityViews/entityDescription/EntityDescription";
import { EntityContactView } from "@/react/components/entityViews/contacts/EntityContactView";
import { EntityOwnershipView } from "@/react/components/entityViews/ownership/EntityOwnershipView";
import { MarketingAssetsView } from "@/react/components/entityViews/marketing/MarketingAssetsView";
import { EntityLocationView } from "@/react/components/entityViews/location/EntityLocationView";
import { LOCATION_GEO_BUTTON_CONFIG } from "@/other/constants/AddressGeoButtonConfig.jsx";
import { geoLocationButtonUrlHelper } from "@/other/utils/utils.jsx";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/modern/components/ui/hover-card";
import { cn } from "@/modern/utils";
import { Timeline } from "@/react/components/entityViews/timeline/Timeline";
import { EntityBudgetView } from "@/react/components/entityViews/budget/EntityBudgetView";
import { formatLocationLines } from "@/react/components/entityViews/location/utils/locationDisplay";

export type EntityTypeKey = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

export interface EntityWidgetProps {
  record: EntityRecord;
  entityType: EntityTypeKey;
  onClose?: () => void;
  /** Full header text from grid opener, e.g. "Task Name | Media" */
  headerTitle?: string;
}

// --- Widget Config ---

export type WidgetId =
  | "timeline"
  | "description"
  | "media"
  | "documents"
  | "notes"
  | "checklists"
  | "location"
  | "ownership"
  | "contacts"
  | "budget"
  | "marketing";

export interface WidgetConfig {
  id: WidgetId;
  title: string;
  color: TileColor;
  component: React.ComponentType<EntityWidgetProps>;
  defaultColSpan: number;
  defaultRowSpan: number;
  defaultVisible: boolean;
}

export const WIDGET_REGISTRY: Record<WidgetId, WidgetConfig> = {
  timeline:    { id: "timeline", title: "Timeline", color: "red", component: Timeline, defaultColSpan: 1, defaultRowSpan: 3, defaultVisible: true },
  description: { id: "description", title: "Description", color: "indigo", component: EntityDescription, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  media:       { id: "media", title: "Media", color: "cyan", component: EntityMediaView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  documents:   { id: "documents", title: "Documents", color: "blue", component: EntityDocumentView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  notes:       { id: "notes", title: "Notes", color: "green", component: EntityNotesView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  checklists:  { id: "checklists", title: "Checklists", color: "blue", component: EntityChecklistView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  location:    { id: "location", title: "Location", color: "orange", component: EntityLocationView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  ownership:   { id: "ownership", title: "Ownership", color: "purple", component: EntityOwnershipView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  contacts:    { id: "contacts", title: "Contacts", color: "rose", component: EntityContactView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
  budget:      { id: "budget", title: "Budget", color: "amber", component: EntityBudgetView, defaultColSpan: 2, defaultRowSpan: 2, defaultVisible: true },
  marketing:   { id: "marketing", title: "Marketing Assets", color: "teal", component: MarketingAssetsView, defaultColSpan: 1, defaultRowSpan: 2, defaultVisible: true },
};

export function getWidgetConfig(id: WidgetId): WidgetConfig {
  return WIDGET_REGISTRY[id];
}

function pickWidgets(...ids: WidgetId[]): WidgetConfig[] {
  return ids.map((id) => WIDGET_REGISTRY[id]);
}


// --- Header Config ---

export interface HeaderFeaturedItemConfig {
  icon: React.ReactNode;
  label: string;
  getValue: (record: EntityRecord) => string | null | undefined;
  renderer?: (record: EntityRecord, index: number) => React.ReactNode;
}

export interface HeaderMetaItemConfig {
  icon: React.ReactNode;
  label?: string;
  getValue: (record: EntityRecord) => string | null | undefined;
}

export interface EntityHeaderConfig {
  description: (record: EntityRecord) => string | null | undefined;
  featuredItems: HeaderFeaturedItemConfig[];
  metaItems: HeaderMetaItemConfig[];
  tags: (record: EntityRecord) => string[];
}

// --- Entity Config ---

export interface EntityConfig {
  storeKey: string;
  widgets: WidgetConfig[];
  contextMenu?: React.ElementType;
  header: EntityHeaderConfig;
}

export const ENTITY_CONFIGS: Record<EntityTypeKey, EntityConfig> = {
  [ENTITY_TYPES.TASK]: {
    storeKey: STORE_KEYS.taskStore,
    contextMenu: TaskContextMenu,
    widgets: pickWidgets("timeline", "description", "media", "documents", "notes", "checklists", "location", "contacts", "marketing", "ownership", "budget"),
    header: {
      description: (record) => record.description || null,
      tags: (record) => record._tags?.map((t: { tagValue: string }) => t.tagValue).filter(Boolean) ?? [],
      featuredItems: [
        { icon: <Building2 className="w-3.5 h-3.5 shrink-0" />, label: "Client", getValue: (record) =>  record.clientName || null },
        {
          icon: <MapPin className="w-3.5 h-3.5 shrink-0" />,
          label: "Location",
          getValue: (record) => record.location?.streetAddress || record.location?.propertyName || null,
          renderer: (record) => {
            const locationRecord = (record as any).location ?? null;
            const { heading, subLine, hasCoords, lat, lon } = formatLocationLines(locationRecord);

            if (!heading && !subLine) return null;
            return (
              <HoverCard openDelay={100} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <span className="inline-flex items-center gap-2 cursor-default min-w-0 max-w-full">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate text-sm text-foreground">
                      {heading && <span className="font-medium">{heading}</span>}
                      {heading && subLine && <span className="text-muted-foreground mx-1.5">•</span>}
                      {subLine && <span className="text-muted-foreground">{subLine}</span>}
                    </span>
                  </span>
                </HoverCardTrigger>
                {hasCoords && (
                  <HoverCardContent className="p-1 w-auto min-w-52" align="start">
                    {LOCATION_GEO_BUTTON_CONFIG.map(({ title, icon, url }, i) => (
                      <a
                        key={title}
                        href={geoLocationButtonUrlHelper(url, { lat, lon })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          "group flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm text-foreground no-underline hover:bg-accent/80 transition-colors",
                          i < LOCATION_GEO_BUTTON_CONFIG.length - 1 && "border-b border-border"
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <img src={icon} className="w-4 h-4 object-contain flex-shrink-0" alt={title} />
                          <span className="font-medium">{title}</span>
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity -rotate-45" />
                      </a>
                    ))}
                  </HoverCardContent>
                )}
              </HoverCard>
            );
          },
        },
        { icon: <Layers className="w-3.5 h-3.5 shrink-0" />, label: "Category", getValue: (record) => record._category?.tagValue || null },
      ],
      metaItems: [
        { icon: <Activity className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Status", getValue: (record) => record._status?.tagValue || null },
        { icon: <Users className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Team", getValue: (record) => record._team?.name || null },
        { icon: <Milestone className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Phase", getValue: (record) => record._phase?.tagValue || null },
      ],
    },
  },
  [ENTITY_TYPES.RESOURCE]: {
    storeKey: STORE_KEYS.resourceStore,
    contextMenu: ResourceContextMenu,
    widgets: pickWidgets("timeline", "media", "documents", "marketing", "notes", "checklists", "contacts", "ownership"),
    header: {
      description: (record) => record.description || null,
      tags: (record) => record._tags?.map((t: { tagValue: string }) => t.tagValue).filter(Boolean) ?? [],
      featuredItems: [
        { icon: <Users className="w-3.5 h-3.5 shrink-0" />, label: "Team", getValue: (record) => record._team?.name || null },
        { icon: <MapPin className="w-3.5 h-3.5 shrink-0" />, label: "Location", getValue: (record) => record.location || null },
        { icon: <Layers className="w-3.5 h-3.5 shrink-0" />, label: "Type", getValue: (record) => record.typeValue || null },
      ],
      metaItems: [
        { icon: <Activity className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Status", getValue: (record) => record._status?.tagValue || null },
        { icon: <Layers className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Category", getValue: (record) => record._category?.tagValue || null },
        { icon: <GitBranch className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Job Title", getValue: (record) => record.jobTitle || null },
      ],
    },
  },
  [ENTITY_TYPES.CLIENT]: {
    storeKey: STORE_KEYS.clientStore,
    contextMenu: ClientContextMenu,
    widgets: pickWidgets("timeline", "media", "documents", "marketing", "notes", "checklists", "contacts", "ownership"),
    header: {
      description: (record) => record.address || null,
      tags: (record) => record._tags?.map((t: { tagValue: string }) => t.tagValue).filter(Boolean) ?? [],
      featuredItems: [
        {
          icon: <Globe className="w-3.5 h-3.5 shrink-0" />,
          label: "Website",
          getValue: (record) => record.website || null,
          renderer: (record, index) => (
            <a
              href={record.website?.startsWith("http") ? record.website : `https://${record.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-foreground hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {index > 0 && <span className="text-border mx-0.5">·</span>}
              <Globe className="w-3.5 h-3.5 shrink-0" />
              <span>{record.website}</span>
            </a>
          ),
        },
        { icon: <MapPin className="w-3.5 h-3.5 shrink-0" />, label: "Address", getValue: (record) => record.address || null },
        { icon: <Layers className="w-3.5 h-3.5 shrink-0" />, label: "Category", getValue: (record) => record._category?.tagValue || null },
      ],
      metaItems: [
        { icon: <Activity className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Status", getValue: (record) => record._status?.tagValue || null },
        { icon: <Users className="w-3.5 h-3.5 text-primary shrink-0" />, label: "Team", getValue: (record) => record._team?.name || null },
      ],
    },
  }
};
